import {
    AlertInterval,
    DeviceCare,
    DeviceCareInstallation,
    MetricsStatus,
    Report,
    SmtpStatus,
} from '@myrtls/api-interfaces';
import { Action, createReducer, on } from '@ngrx/store';
import { MetricWithData } from '../../shared/models/metric-with-data.model';
import { DeviceCarePeriod } from '../../shared/models/device-care-period.model';
import {
    getCurrentSiteAlertsFailureAction,
    getCurrentSiteAlertsRequestAction,
    getCurrentSiteAlertsSuccessAction,
    getSiteAlertsFailureAction,
    getSiteAlertsRequestAction,
    getSiteAlertsSuccessAction,
    getSiteDeviceCareConfigRequestAction,
    getSiteDeviceCareConfigSuccessAction,
    getSiteDeviceCareInstallationSuccessAction,
    getSiteMetricDataSuccessAction,
    getSiteMetricDashboardDataSuccessAction,
    getSiteMetricFilteringDataSuccessAction,
    getSiteMetricsConfigSuccessAction,
    getSiteMetricsStatusSuccessAction,
    getSiteReportsFailureAction,
    getSiteReportsRequestAction,
    getSiteReportsSuccessAction,
    getSiteSmtpStatusSuccessAction,
    selectPeriodDateRangeAction,
    setAlertsOnly,
    setIsReport,
    updateSiteMetricSettingsFailureAction,
    updateSiteMetricSettingsRequestAction,
    updateSiteMetricSettingsSuccessAction,
    updateSiteReportMetricsAction,
    updateSiteReportSettingsFailureAction,
    updateSiteReportSettingsRequestAction,
    updateSiteReportSettingsSuccessAction,
} from './monitoring.actions';
import { selectSiteAction } from '../sites/sites.actions';

export interface DeviceCareInfo extends DeviceCare {
    selectedPeriod: DeviceCarePeriod | null;
}

export interface MonitoringState {
    metricsStatus: MetricsStatus;
    alertsOnly: boolean;
    isReport: boolean;
    focus: string | null;
    alerts: AlertInterval[] | null;
    currentAlerts: AlertInterval[];
    alertsLoading: boolean;
    currentAlertsLoading: boolean;
    reports: Report[];
    reportsLoading: boolean;
    smtpStatus: SmtpStatus | null;
    installationParameters: DeviceCareInstallation | null;
    metricsUpdateLoading: boolean;
    reportUpdateLoading: boolean;
    metrics: MetricWithData[] | null;
    reportMetrics: string[];
    deviceCare: DeviceCareInfo | null;
}

const initialState: MonitoringState = {
    metricsStatus: {
        system: false,
        studio: false,
    },
    alertsOnly: false,
    isReport: false,
    focus: null,
    alerts: null,
    currentAlerts: [],
    alertsLoading: false,
    currentAlertsLoading: false,
    reports: [],
    reportsLoading: false,
    smtpStatus: null,
    installationParameters: null,
    metricsUpdateLoading: false,
    reportUpdateLoading: false,
    metrics: null,
    reportMetrics: [],
    deviceCare: null,
};

const reducer = createReducer(
    initialState,
    on(selectSiteAction, state => ({
        ...state,
        metrics: null,
        reportMetrics: [],
        deviceCare: null,
    })),
    on(setAlertsOnly, (state, { alertsOnly }) => ({
        ...state,
        alertsOnly,
    })),
    on(setIsReport, (state, { isReport }) => ({
        ...state,
        isReport,
    })),
    on(getSiteMetricsStatusSuccessAction, (state, { status }) => ({
        ...state,
        metricsStatus: status,
    })),
    on(getSiteDeviceCareConfigRequestAction, (state, { focus }) => ({
        ...state,
        focus: focus || null,
    })),
    on(getSiteDeviceCareInstallationSuccessAction, (state, { installation }) => ({
        ...state,
        installationParameters: installation,
    })),
    on(updateSiteMetricSettingsRequestAction, state => ({
        ...state,
        metricsUpdateLoading: true,
    })),
    on(updateSiteMetricSettingsFailureAction, state => ({
        ...state,
        metricsUpdateLoading: false,
    })),
    on(updateSiteReportSettingsRequestAction, state => ({
        ...state,
        reportUpdateLoading: true,
    })),
    on(updateSiteReportSettingsFailureAction, state => ({
        ...state,
        reportUpdateLoading: false,
    })),
    on(getCurrentSiteAlertsRequestAction, state => ({
        ...state,
        currentAlertsLoading: true,
    })),
    on(getSiteAlertsRequestAction, state => ({
        ...state,
        alertsLoading: true,
    })),
    on(getCurrentSiteAlertsSuccessAction, (state, { alerts }) => ({
        ...state,
        currentAlertsLoading: false,
        currentAlerts: alerts,
    })),
    on(getCurrentSiteAlertsFailureAction, state => ({
        ...state,
        currentAlertsLoading: false,
    })),
    on(getSiteAlertsSuccessAction, (state, { alerts }) => ({
        ...state,
        alertsLoading: false,
        alerts,
    })),
    on(getSiteAlertsFailureAction, state => ({
        ...state,
        alertsLoading: false,
    })),
    on(getSiteReportsRequestAction, state => ({
        ...state,
        reportsLoading: true,
    })),
    on(getSiteReportsSuccessAction, (state, { reports }) => ({
        ...state,
        reportsLoading: false,
        reports,
    })),
    on(getSiteReportsFailureAction, state => ({
        ...state,
        reportsLoading: false,
    })),
    on(getSiteSmtpStatusSuccessAction, (state, { smtpStatus }) => ({
        ...state,
        smtpStatus,
    })),
    on(getSiteMetricsConfigSuccessAction, (state, { metrics }) => ({
        ...state,
        metrics: metrics.map(metric => ({ ...metric, loading: true })),
    })),
    on(getSiteMetricDataSuccessAction, (state, { metricId, data, range, from, to }) => ({
        ...state,
        metrics: state.metrics?.map(metric =>
            metric.id === metricId ? { ...metric, data, range, from, to } : metric,
        ) ?? null,
    })),
    on(getSiteMetricDashboardDataSuccessAction, (state, { metricId, data }) => ({
        ...state,
        metrics: state.metrics?.map(metric =>
            metric.id === metricId ? { ...metric, dashboardData: data } : metric,
        ) ?? null,
    })),
    on(getSiteMetricFilteringDataSuccessAction, (state, { metricId, data }) => ({
        ...state,
        metrics: state.metrics?.map(metric =>
            metric.id === metricId ? { ...metric, filteringData: data } : metric,
        ) ?? null,
    })),
    on(updateSiteMetricSettingsSuccessAction, (state, { metrics }) => ({
        ...state,
        metricsUpdateLoading: false,
        metrics: metrics.map(updated => ({
            ...(state.metrics?.find(m => m.id === updated.id) ?? {}),
            ...updated,
        })) as MetricWithData[],
    })),
    on(updateSiteReportMetricsAction, (state, { metrics }) => ({
        ...state,
        reportMetrics: metrics,
    })),
    on(getSiteDeviceCareConfigSuccessAction, (state, { config, period }) => ({
        ...state,
        deviceCare: {
            ...state.deviceCare,
            selectedPeriod: period || null,
            ...config,
        },
    })),
    on(updateSiteReportSettingsSuccessAction, (state, { config }) => ({
        ...state,
        deviceCare: {
            ...state.deviceCare,
            ...config,
        } as DeviceCareInfo,
        reportUpdateLoading: false,
    })),
    on(selectPeriodDateRangeAction, (state, { dateRange, from, to }) => ({
        ...state,
        deviceCare: {
            ...state.deviceCare,
            selectedPeriod: { dateRange, from, to },
        } as DeviceCareInfo,
        metrics: (state.metrics || []).map(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ({ data, ...metaData }) => metaData,
        ),
    })),
);

export function monitoringReducer(
    state: MonitoringState | undefined,
    action: Action,
): MonitoringState {
    return reducer(state, action);
}
